import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, User, Mail, Phone, Copy, ExternalLink, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { TRYOUT, CONTACT } from "../mock";
import { registerTryout, confirmTryoutPayment } from "../api";
import Seo from "../components/Seo";

const TIMES = ["Weekday mornings (6 AM)", "Weekday afternoons (4–5 PM)", "Weekday evenings (6–7 PM)", "Saturday mornings", "Not sure yet"];

const PaymentCard = ({ p, reference }) => (
  <div className="rounded-xl border border-neutral-200 p-4" data-testid={`payment-${p.id}`}>
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="font-semibold text-[#1A1A1A]">{p.name}</p>
        <p className="text-sm text-neutral-600">{p.note}</p>
      </div>
      <button
        type="button"
        onClick={() => navigator.clipboard?.writeText(p.handle)}
        className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-br-green hover:underline"
        data-testid={`copy-${p.id}`}
      >
        <Copy size={13} /> Copy
      </button>
    </div>
    <p className="mt-2 font-mono text-sm bg-neutral-50 rounded-lg px-3 py-2 break-all">{p.handle}</p>
    {p.url && (
      <a href={p.id === "venmo" ? `https://venmo.com/alamedabjj?txn=pay&amount=99&note=${encodeURIComponent("Alameda BJJ Tryout " + reference)}` : p.url} target="_blank" rel="noreferrer" data-testid={`open-${p.id}`}>
        <Button variant="outline" className="mt-3 w-full h-10 rounded-full border-neutral-300 text-sm font-semibold">
          {p.id === "venmo" ? "Pay $99 in Venmo" : `Open ${p.name}`} <ExternalLink size={14} className="ml-1" />
        </Button>
      </a>
    )}
  </div>
);

const Tryout = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", program: "", preferred_time: "", marketing_consent: false, sms_consent: false });
  const [loading, setLoading] = useState(false);
  const [reg, setReg] = useState(null);
  const [claimed, setClaimed] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.phone || !form.program) {
      toast({ title: "A few details missing", description: "Please add your name, valid email, phone and program." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await registerTryout(form);
      setReg(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast({ title: "Something went wrong", description: `Please try again or call ${CONTACT.phone}.` });
    } finally {
      setLoading(false);
    }
  };

  const claim = async () => {
    try {
      await confirmTryoutPayment(reg.id);
      setClaimed(true);
    } catch (err) {
      toast({ title: "Couldn't send", description: `Please call or text ${CONTACT.phone} to confirm your payment.` });
    }
  };

  return (
    <div className="pt-24">
      <Seo
        title="$99 New Student 30-Day BJJ Tryout | Alameda BJJ"
        description="Try Brazilian Jiu-Jitsu in Alameda for 30 days: 2 classes per week plus a free uniform for $99. New students only, one-time payment, no automatic renewal."
        path="/tryout"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "$99 Tryout", path: "/tryout" }]}
      />
      <section className="bg-[#0E1613] py-14 text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <span className="inline-flex items-center rounded-full bg-br-yellow text-[#1A1A1A] px-4 py-1.5 text-xs font-bold tracking-wide">NEW STUDENT OFFER</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold leading-tight">
            {TRYOUT.name} <span className="text-br-yellow">{TRYOUT.price}</span>
          </h1>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
            {TRYOUT.points.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-neutral-200"><CheckCircle2 size={18} className="text-br-green shrink-0" /> {p}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          {!reg ? (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 sm:p-8 space-y-4" data-testid="tryout-form">
              <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">Step 1 — Register</h2>
              <p className="text-sm text-neutral-600">Tell us who's training. Payment instructions appear on the next step.</p>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="tryout-pay-methods">
                <span className="text-neutral-500 self-center">Pay {TRYOUT.price} easily with:</span>
                {TRYOUT.payments.map((p) => (
                  <span key={p.id} className="rounded-full bg-neutral-100 px-3 py-1 font-semibold text-[#1A1A1A]">{p.name} · {p.handle}</span>
                ))}
              </div>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" size={17} />
                <Input value={form.name} onChange={change("name")} placeholder="Student name*" data-testid="tryout-name" className="pl-11 h-12 rounded-xl border-neutral-300" />
              </div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" size={17} />
                <Input type="email" value={form.email} onChange={change("email")} placeholder="Email*" data-testid="tryout-email" className="pl-11 h-12 rounded-xl border-neutral-300" />
              </div>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" size={17} />
                <Input type="tel" value={form.phone} onChange={change("phone")} placeholder="Mobile phone*" data-testid="tryout-phone" className="pl-11 h-12 rounded-xl border-neutral-300" />
              </div>
              <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                <SelectTrigger className="h-12 rounded-xl border-neutral-300" data-testid="tryout-program"><SelectValue placeholder="Program interest*" /></SelectTrigger>
                <SelectContent>{TRYOUT.programs.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                <SelectTrigger className="h-12 rounded-xl border-neutral-300" data-testid="tryout-time"><SelectValue placeholder="Preferred class time" /></SelectTrigger>
                <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <label className="flex items-start gap-3 text-xs text-neutral-600 leading-snug cursor-pointer">
                <Checkbox checked={form.sms_consent} onCheckedChange={(v) => setForm({ ...form, sms_consent: v === true })} data-testid="tryout-sms-consent" className="mt-0.5" />
                <span>Text me when my payment is confirmed and a reminder before class. Msg & data rates may apply. Reply STOP to opt out.</span>
              </label>
              <label className="flex items-start gap-3 text-xs text-neutral-600 leading-snug cursor-pointer">
                <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: v === true })} data-testid="tryout-consent" className="mt-0.5" />
                <span>Yes, email me training tips and membership specials from Alameda BJJ. Optional — unsubscribe anytime.</span>
              </label>
              <Button type="submit" disabled={loading} data-testid="tryout-submit" className="w-full h-13 py-6 bg-br-green hover:bg-[#00822f] text-white rounded-full text-base font-bold disabled:opacity-70">
                {loading ? "Saving..." : `Continue to Payment — ${TRYOUT.price}`}
              </Button>
              <p className="text-xs text-neutral-400 text-center">One-time payment. No automatic renewal. New students only.</p>
            </form>
          ) : (
            <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 sm:p-8" data-testid="tryout-payment-step">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-br-green/10 flex items-center justify-center"><CheckCircle2 className="text-br-green" size={24} /></div>
                <div>
                  <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">Step 2 — Send your {TRYOUT.price} payment</h2>
                  <p className="text-sm text-neutral-600">Registration saved for {reg.name}. We also emailed these instructions to {reg.email}.</p>
                </div>
              </div>
              <div className="mt-5 rounded-xl bg-br-yellow/15 border border-br-yellow/40 p-4 text-sm text-[#1A1A1A]">
                Please include this reference in your payment note: <span className="font-mono font-bold" data-testid="tryout-reference">{reg.reference}</span>
              </div>
              <div className="mt-5 grid gap-4">
                {TRYOUT.payments.map((p) => <PaymentCard key={p.id} p={p} reference={reg.reference} />)}
              </div>
              {!claimed ? (
                <Button onClick={claim} data-testid="tryout-paid-btn" className="mt-6 w-full h-12 bg-[#1A1A1A] hover:bg-black text-white rounded-full font-semibold">
                  I've sent my payment
                </Button>
              ) : (
                <div className="mt-6 rounded-xl bg-br-green/10 p-4 text-sm text-[#1A1A1A] flex gap-2" data-testid="tryout-claimed">
                  <Clock size={18} className="text-br-green shrink-0 mt-0.5" />
                  Thanks! Your spot is <strong className="mx-1">pending</strong> until we confirm the payment — usually within one business day. We'll email and call you to schedule your first class.
                </div>
              )}
              <p className="mt-4 text-xs text-neutral-500 text-center">
                Status: <span className="font-semibold">Pending payment confirmation</span>. Questions? Call or text <a href={CONTACT.phoneHref} className="text-br-green font-semibold">{CONTACT.phone}</a>.
              </p>
              <p className="mt-3 text-center text-sm"><Link to="/schedule" className="text-br-green font-semibold">View the class schedule</Link></p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Tryout;
